import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import SendIcon from '@mui/icons-material/Send';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import apiClient from '../../../services/apiClient';
import { useAIAssistantContext } from '../../../contexts/AIAssistantContext';
import axios from 'axios';

const isHtml = (str) => /<[a-z][\s\S]*>/i.test(str);
const currentTime = new Date().toLocaleTimeString([], {
  hour: "2-digit",
  minute: "2-digit",
});

const initialMessages = [
  {
    id: 1,
    sender: "assistant",
    text: "Hello! What would you like to know?",
    time: currentTime,
  },
];

const renderMessageText = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={index} style={{ fontWeight: 700 }}>
          {part.slice(2, -2)}
        </strong>
      );
    }

    return part;
  });
};

const AIAssistant = ({ onClose, batchId }) => {
  const [messages, setMessages] = useState(initialMessages);
  const [inputVal, setInputVal] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef(null);

  const { getPayload } = useAIAssistantContext() || {};

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const handleSend = async () => {
    if (!inputVal.trim()) return;

    const userMsg = {
      id: Date.now(),
      sender: 'user',
      text: inputVal,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputVal('');
    setIsTyping(true);

    try {
      // 1. Get dynamic payload from active screen if available
      let payload = null;
      if (getPayload) {
        payload = await getPayload();
      }

      // 2. Fallback to basic payload if no dynamic one
      if (!payload) {
        payload = {
          screen: "Global",
          question: userMsg.text
        };
      } else {
        payload.question = userMsg.text;
      }

      console.log("AI Assistant Request Payload:", payload);

      // 3. Make the API Call
      const aiEndpoint = process.env.REACT_APP_AI_ASSISTANT_URL || 'https://dev3-bleubird-aceai-workflow.data-aces.com';

      let replyText = '';
      try {
        const response = await axios.post(`${aiEndpoint}/webhook/mdm-ai-assistant/chat`, payload);
        const data = response.data;
        replyText = data.answer || data.reply || data.message || "I successfully processed your request, but received an empty response.";
      } catch (err) {
        console.error("AI Request failed:", err);
        replyText = "I'm sorry, I'm currently unable to reach the AI service.";
      }

      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          sender: 'assistant',
          text: replyText,
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
      ]);
      setIsTyping(false);
      return; // Exit early so mock code below doesn't run
    } catch (err) {
      console.error("Error communicating with AI Assistant API:", err);
    }

    setTimeout(() => {
      let replyText = '';
      const textLower = userMsg.text.toLowerCase();

      if (textLower.includes('status') || textLower.includes('create')) {
        replyText = `This request is in status **MERGED** for batch **${batchId || '3017'}**. It was automatically resolved by the AceMDM autonomous processing service.`;
      } else if (
        textLower.includes('why') ||
        textLower.includes('merge') ||
        textLower.includes('reason')
      ) {
        replyText =
          'The merge was performed autonomously because the match score crossed the threshold (74%). The principal identifiers verified the records represent the same provider.';
      } else if (
        textLower.includes('workflow') ||
        textLower.includes('step') ||
        textLower.includes('timeline')
      ) {
        replyText = `Here are the workflow steps for this request:
1. DCR Created
2. Agent Triggered Automatically
3. AI Reads DCR Metadata
4. Internet / Master Data Lookup
5. Confidence Evaluation

You can also view the full timeline in the DCR Workflow & Decision Timeline panel.`;
      } else {
        replyText =
          'I can help explain the request status, merge reasoning, matching records, and workflow timeline for this screen.';
      }

      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          sender: 'assistant',
          text: replyText,
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
      ]);
      setIsTyping(false);
    }, 1000);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 4,
        // border: '1px solid #E2E8F0',
        backgroundColor: '#ffffff',
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #F1F5F9',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(8px)',
          zIndex: 10,
        }}
      >
        <Stack direction="row" spacing={1.2} alignItems="center">
          <div style={{ backgroundColor: '#E3F2FD', width: '40px', height: '40px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1E88E5', boxShadow: '0 2px 8px rgba(30,136,229,0.1)' }} >
            <i className="fa-solid fa-robot" style={{ fontSize: '18px' }} ></i>
          </div>
          <Box>
            <Stack direction="row" spacing={0.8} alignItems="center">
              <Typography
                sx={{  fontWeight: 700, color: '#1E293B', lineHeight: 1.2 }}
              >
                AI Assistant
              </Typography>
            </Stack>
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.3,  color: '#64748B' }}
            >
              Your contextual expert for this screen
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={0.1}>
          <IconButton size="small" onClick={onClose} sx={{ color: '#94A3B8', p: 0.5, '&:hover': { color: '#475569', backgroundColor: '#F1F5F9' } }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Stack>
      </Box>

      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 2,
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          backgroundColor: '#FCFDFF',
          '&::-webkit-scrollbar': {
            width: 5,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha('#CBD5E1', 0.8),
            borderRadius: 999,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
        }}
      >
        {messages.map((msg) => {
          const isUser = msg.sender === 'user';

          return (
            <Box
              key={msg.id}
              sx={{
                alignSelf: isUser ? 'flex-end' : 'flex-start',
                maxWidth: isUser ? '82%' : '95%',
              }}
            >
              {isUser ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, #1E88E5 0%, #1565C0 100%)',
                      color: '#ffffff',
                      px: 1.8,
                      py: 1.2,
                      borderRadius: '18px 18px 4px 18px',
                      boxShadow: '0 4px 12px rgba(30, 136, 229, 0.2)',
                    }}
                  >
                    <Typography sx={{  fontWeight: 400, lineHeight: 1.45 }}>
                      {msg.text}
                    </Typography>
                  </Box>
                  <Typography sx={{  color: '#94A3B8', mt: 0.6, mr: 0.4 }}>
                    {msg.time}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      bgcolor: '#ffffff',
                      border: '1px solid #E2E8F0',
                      borderRadius: '18px 18px 18px 4px',
                      px: 1.8,
                      py: 1.2,
                      boxShadow: '0 2px 6px rgba(15,23,42,0.04)',
                    }}
                  >
                    {isHtml(msg.text) ? (
                      <Box
                        dangerouslySetInnerHTML={{ __html: msg.text }}
                        sx={{
                          
                          color: '#334155',
                          lineHeight: 1.6,
                          '& a': { color: '#1E88E5', textDecoration: 'none' },
                          '& a:hover': { textDecoration: 'underline' },
                          '& ul, & ol': { margin: '8px 0', paddingLeft: '24px' },
                          '& table': { borderCollapse: 'collapse', width: '100%', marginTop: '12px' },
                          '& th, & td': { border: '1px solid #E2E8F0', padding: '6px 12px' },
                          '& th': { backgroundColor: '#F8FAFC', fontWeight: 600 },
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          
                          color: '#334155',
                          lineHeight: 1.6,
                          whiteSpace: 'pre-line',
                        }}
                      >
                        {renderMessageText(msg.text)}
                      </Typography>
                    )}
                  </Box>
                  <Typography sx={{  color: '#94A3B8', mt: 0.6, ml: 0.4 }}>
                    {msg.time}
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}

        {isTyping ? (
          <Box
            sx={{
              alignSelf: 'flex-start',
              bgcolor: '#ffffff',
              border: '1px solid #E2E8F0',
              borderRadius: '18px 18px 18px 4px',
              px: 1.8,
              py: 1.5,
              width: 58,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 0.6,
              boxShadow: '0 2px 6px rgba(15,23,42,0.04)',
            }}
          >
            {[0, 0.15, 0.3].map((delay) => (
              <Box
                key={delay}
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: '#94A3B8',
                  animation: `typingBounce 1.4s infinite ease-in-out ${delay}s`,
                  '@keyframes typingBounce': {
                    '0%, 80%, 100%': { transform: 'scale(0)' },
                    '40%': { transform: 'scale(1)' },
                  },
                }}
              />
            ))}
          </Box>
        ) : null}
      </Box>

      <Box sx={{ px: 2, pt: 1.5, pb: 2, backgroundColor: '#ffffff', borderTop: '1px solid #F1F5F9' }}>
        <TextField
          fullWidth
          size="medium"
          placeholder="Ask a question about this screen..."
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSend();
          }}
          InputProps={{
            endAdornment: (
              <IconButton
                size="small"
                onClick={handleSend}
                sx={{
                  color: '#1565C0',
                  // backgroundColor: '#1E88E5',
                  p: 0.8,
                  mr: -0.5,
                  // boxShadow: '0 2px 8px rgba(30,136,229,0.3)',
                  // '&:hover': { backgroundColor: '#1565C0' }
                }}
              >
                <SendIcon sx={{ fontSize: 16 }} />
              </IconButton>
            ),
            sx: {
              borderRadius: '24px',
              backgroundColor: '#ffffff',
              
              pl: 1.5,
              pr: 1.5,
              py: 0.2,
              border: '1px solid #E2E8F0',
              transition: 'all 0.2s',
              '& input': {
                backgroundColor: 'transparent',
              },
              '& input::placeholder': {
                color: '#94A3B8',
                opacity: 1,
              },
              '& fieldset': {
                border: 'none',
                display: 'none',
              },
              '&:hover': {
                borderColor: '#CBD5E1',
              },
              '&.Mui-focused': {
                borderColor: '#1E88E5',
                boxShadow: '0 0 0 1px rgba(30, 136, 229, 0.2)',
              },
            },
          }}
        />
        <Typography
          sx={{
            mt: 1.2,
            
            lineHeight: 1.4,
            color: '#94A3B8',
            textAlign: 'center',
          }}
        >
          AI responses are based on the data shown on this screen and may not reflect real-time
          updates.
        </Typography>
      </Box>
    </Paper>
  );
};

AIAssistant.propTypes = {
  onClose: PropTypes.func.isRequired,
  batchId: PropTypes.string,
};

export default AIAssistant;
